import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styles from './CustomerAdmin.module.css';

type Customer = {
  id: string;
  name?: string;
  email?: string;
  created_at?: string;
};

type CustomerFormData = {
  name: string;
  email: string;
};

async function fetchCustomers(): Promise<Customer[]> {
  const response = await fetch('/api/customers');
  if (!response.ok) throw new Error(`Error: ${response.status}`);
  const data = await response.json();
  return (data?.customers ?? data) as Customer[];
}

export default function CustomerAdmin() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>({ name: '', email: '' });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);

  const { data: customers = [], isLoading, isError } = useQuery({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
  });

  const saveMutation = useMutation({
    mutationFn: async (vars: { formData: CustomerFormData; editMode: boolean; currentId: string | null }) => {
      const { formData, editMode, currentId } = vars;
      const url = editMode ? `/api/customers/${currentId}` : '/api/customers';
      const method = editMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save customer');
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setMessage(vars.editMode ? 'Customer updated successfully' : 'Customer created successfully');
      resetForm();
    },
    onError: (err: Error) => setMessage(err.message || 'Failed to save customer'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/customers/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete customer');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setMessage('Customer deleted successfully');
    },
    onError: (err: Error) => setMessage(err.message || 'Failed to delete customer'),
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({ name: '', email: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveMutation.mutate({ formData, editMode, currentId });
  };

  const handleEdit = (customer: Customer) => {
    setFormData({ name: customer.name || '', email: customer.email || '' });
    setEditMode(true);
    setCurrentId(customer.id);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    deleteMutation.mutate(id);
  };

  const error = isError ? 'Failed to fetch customers' : null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <h1 className={styles.title}>Customer Management</h1>

      <div className={styles.content}>
        <div className={styles.formSection}>
          <h2>{editMode ? 'Edit Customer' : 'Add New Customer'}</h2>

          {message && <div className={styles.errorMessage}>{message}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Customer name (optional)"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Customer email (optional)"
              />
            </div>

            <div className={styles.formActions}>
              <button type="submit" className={styles.submitButton}>
                {editMode ? 'Update Customer' : 'Add Customer'}
              </button>

              {editMode && (
                <button type="button" className={styles.cancelButton} onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className={styles.listSection}>
          <h2>Customer List</h2>

          {isLoading ? (
            <div className={styles.loading}>Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className={styles.emptyMessage}>No customers found</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.id} className={styles.customerRow}>
                    <td className={styles.idCell}>{customer.id}</td>
                    <td>{customer.name || '-'}</td>
                    <td>{customer.email || '-'}</td>
                    <td>{formatDate(customer.created_at)}</td>
                    <td className={styles.actionCell}>
                      <button onClick={() => handleEdit(customer)} className={styles.editButton}>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
